import {
  ArrowBackOutlined,
  ButtonDS,
  DragAndDrop,
  TextCapitalized,
  triggerToast,
} from "@qivia/ui";
import { Spacer } from "@qivia/ui/src/designSystem/components/Spacer";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { selectCompany } from "../../../homeSlice";
import { uploadDriverPlanningsFileThunk } from "../driverPlanningsSlice";
import "../../../../../i18n/i18n";
import * as XLSX from "xlsx";
import {
  getDriverPlanningHeaderErrors,
  type DriverPlanningImportError,
  getDriverPlanningLineErrors,
} from "../driverPlanningFormat/driverPlanningsErrors";
import { DriverPlanningDisplayFile } from "./driverPlanningDisplayFile";
import {
  DriverPlanningImportStepTitle,
  DriverPlanningTemplateDownload,
} from "./driverPlanningTemplateDownload";

export const ImportDriverPlanningsModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
}) => {
  const { t } = useTranslation();

  if (!props.visible) {
    return;
  }

  return (
    <StyledFrame>
      <Spacer x={2.5} />
      <StyledColumn>
        <Spacer y={2.5} />
        <ReturnButton onCloseModal={props.onCloseModal} />
        <Spacer y={2.5} />
        <StyledContentImport>
          <StyledTitle>
            <TextCapitalized>{t(`trips.importPlanning.title`)}</TextCapitalized>
          </StyledTitle>
          <Spacer y={2} />
          <StyledFrameContainer>
            <FileImport onCloseModal={props.onCloseModal} />
          </StyledFrameContainer>
          <Spacer y={2.5} />
        </StyledContentImport>
        <Spacer y={2.5} />
      </StyledColumn>
      <Spacer x={2.5} />
    </StyledFrame>
  );
};

const ReturnButton = (props: { onCloseModal: () => void }) => {
  const { t } = useTranslation();
  return (
    <div>
      <ButtonDS
        text={t("trips.importPlanning.button.return")}
        leftIcon={<ArrowBackOutlined />}
        onClick={() => props.onCloseModal()}
        buttonType={"secondary"}
        format={"hug"}
      />
    </div>
  );
};

const FileImport = (props: { onCloseModal: () => void }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const [hasExtensionError, setHasExtensionError] = useState<boolean>(false);
  const [errors, setErrors] = useState<DriverPlanningImportError[] | null>(
    null,
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const company = useAppSelector(selectCompany);

  const isValidImport = useMemo(
    () => errors !== null && errors.length === 0,
    [errors],
  );

  const MIME = {
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };

  const checkFile = useCallback(
    (selectedFile: File) => {
      if (selectedFile && selectedFile.type !== MIME.xlsx) {
        setHasExtensionError(true);
        setErrors(null);
      } else if (selectedFile) {
        setHasExtensionError(false);
        setErrors(null);
        const fileReader = new FileReader();
        fileReader.readAsArrayBuffer(selectedFile);
        fileReader.onload = (event) => {
          if (event?.target) {
            try {
              const bufferArray = event.target.result;
              const workBook = XLSX.read(bufferArray, { type: "buffer" });
              const sheet = workBook.Sheets[workBook.SheetNames[0]];
              const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
                header: 1,
                raw: false,
              });
              const errors = [
                ...getDriverPlanningHeaderErrors(rows[0] ?? []),
                ...getDriverPlanningLineErrors(rows),
              ];

              setErrors(errors);
            } catch {
              setErrors(null);
              triggerToast(t("trips.mime.type.failure"), "error");
            }
          }
        };
      } else {
        triggerToast(t("trips.mime.type.failure"), "error");
      }
    },
    [MIME.xlsx, t],
  );

  const onSubmitDriverPlannings = useCallback(() => {
    if (!selectedFile || !company) {
      return;
    }
    void dispatch(
      uploadDriverPlanningsFileThunk({
        file: selectedFile,
        companyUuid: company.uuid,
      }),
    );
    props.onCloseModal();
  }, [selectedFile, company, dispatch, props]);

  const clickToSubmit = useCallback(() => {
    if (isValidImport) onSubmitDriverPlannings();
  }, [isValidImport, onSubmitDriverPlannings]);

  useEffect(() => {
    if (selectedFile) checkFile(selectedFile);
  }, [checkFile, selectedFile]);

  const isFileAlreadyImport = errors !== null && !hasExtensionError;

  return (
    <>
      <DriverPlanningTemplateDownload />
      <Spacer y={2} />
      <DriverPlanningImportStepTitle
        step={2}
        title={t("trips.importPlanning.importFile")}
      />
      <Spacer y={2} />
      {isFileAlreadyImport ? (
        <>
          <DriverPlanningDisplayFile
            selectedFile={selectedFile?.name}
            handleFileChange={setSelectedFile}
            type="REPLACE"
          />
          <Spacer y={1.5} />
          {errors.length > 0 && (
            <StyledFlex>
              <Spacer x={0.75} />
              <StyledColumn>
                <StyledCountErrors>
                  {errors.length}
                  {t(
                    `trips.importPlanning.${
                      errors.length === 1 ? "countError" : "countErrors"
                    }`,
                  )}
                </StyledCountErrors>
                <Spacer y={0.75} />
                <StyledCells>
                  {errors.map((error, index) => (
                    <>
                      <ErrorCell error={error} key={"error" + index} />
                      <Spacer y={0.5} />
                    </>
                  ))}
                </StyledCells>
              </StyledColumn>
              <Spacer x={0.75} />
            </StyledFlex>
          )}
          <Spacer y={1.5} />
          <ButtonDS
            text={t("vehicles.modal.addMultipleVehicles.add")}
            format={"hug"}
            buttonType={"primary"}
            onClick={clickToSubmit}
            disabled={!isValidImport}
          />
        </>
      ) : (
        <StyledFlexCenter>
          <DragAndDrop
            handleFileChange={setSelectedFile}
            body={t("vehicles.modal.addMultipleVehicles.dragAndDrop.subTitle")}
            hasExtensionError={hasExtensionError}
          />
        </StyledFlexCenter>
      )}
    </>
  );
};

const ErrorCell = (props: { error: DriverPlanningImportError }) => {
  const { t } = useTranslation();

  const place = props.error.cell
    ? `${t("trips.importPlanning.cell")} ${props.error.cell.slice(1)}, ${props.error.cell[0]}`
    : t("trips.importPlanning.column");

  const truncatedContent =
    props.error.content && props.error.content.length > 28
      ? `${props.error.content.substring(0, 28)}...`
      : props.error.content;

  return (
    <StyledContainerCell>
      <Spacer x={0.5} />
      <StyledColumn>
        <Spacer y={0.5} />
        <StyledContent>
          <TextCapitalized>{place}</TextCapitalized>
          <StyledTypeErrors>
            {truncatedContent}
            <Spacer y={0.25} />
            <StyledTypeErrorsBottom>
              <TextCapitalized>
                {t(`trips.importPlanning.errorType.${props.error.errorType}`)}
              </TextCapitalized>
            </StyledTypeErrorsBottom>
          </StyledTypeErrors>
        </StyledContent>
        <Spacer y={0.5} />
      </StyledColumn>
      <Spacer x={0.5} />
    </StyledContainerCell>
  );
};

const StyledFlex = styled.div`
  display: flex;
  width: 100%;
`;

const StyledFlexCenter = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledFrame = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  background-color: ${colors["colors/surfaces/background/background_level0"]};
  border-radius: 0.75rem 0 0 0.75rem;
`;

const StyledColumn = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const StyledContentImport = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;
`;

const StyledFrameContainer = styled.div`
  max-width: 39.5rem;
  width: 100%;
  display: flex;
  padding: 3rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow:
    0px 2px 5px 0px rgba(143, 143, 143, 0.15),
    0px 0px 15px 0px rgba(143, 143, 143, 0.15);
  border-radius: 0.75rem;
  border: 0.0625rem solid transparent;
  background:
    linear-gradient(white, white) padding-box,
    linear-gradient(to left, #3cd3ad, #44b2d5) border-box;
  background-color: ${colors["colors/surfaces/background/background_level0"]};
`;

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${typographies["Header/H3"]};
  color: ${colors["colors/text/black"]};
  width: 100%;
`;

const StyledContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const StyledTypeErrors = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const StyledTypeErrorsBottom = styled.div`
  ${typographies["Body/S"]};
  color: ${colors["colors/system/error/error_normal"]};
`;

const StyledContainerCell = styled.div`
  display: flex;
  border-radius: 0.5rem;
  border: 0.0625rem solid ${colors["colors/borders/cards/cards"]};
`;

const StyledCountErrors = styled.div`
  color: ${colors["colors/system/error/error_normal"]};
`;

const StyledCells = styled.div`
  overflow-y: scroll;
  max-height: 20rem;
  width: 100%;
`;
